import {
  Children,
  isValidElement,
  ReactElement,
  ReactNode,
  useMemo
} from "react";
import { Box, BoxProps } from "@mui/material";
import { useListContext } from "react-admin";

import { TableCsvDownloadMenu } from "./TableCsvDownloadMenu";
import { CsvColumn, CsvExportDefinition } from "./types";
import {
  useTableCsvExport,
  UseTableCsvExportOptions
} from "./useTableCsvExport";

interface ExportFieldProps {
  children?: ReactNode;
  exportable?: boolean;
  exportKey?: string;
  source?: string;
}

const exportFieldKeys = (children: ReactNode): string[] =>
  Children.toArray(children).flatMap((child) => {
    if (!isValidElement<ExportFieldProps>(child)) {
      return [];
    }

    if (child.props.exportable === false) {
      return [];
    }

    const key = child.props.exportKey || child.props.source;
    return key ? [key] : exportFieldKeys(child.props.children);
  });

/**
 * Selects export definitions using the fields that are actually mounted in the
 * Datagrid. This keeps conditional JSX as the single source of column
 * visibility and ordering.
 */
export const getActiveCsvColumns = <T,>(
  columns: readonly CsvColumn<T>[],
  children: ReactNode
): CsvColumn<T>[] => {
  const columnsByKey = new Map<string, CsvColumn<T>>();
  columns.forEach((column) => {
    columnsByKey.set(column.source || column.key, column);
  });

  return exportFieldKeys(children).flatMap((key) => {
    const column = columnsByKey.get(key);

    if (!column && key !== "id" && process.env.NODE_ENV !== "production") {
      throw new Error(
        `Missing CSV export column for mounted field "${key}". ` +
          "Add it to the export definition or set exportable={false}."
      );
    }

    return column ? [column] : [];
  });
};

type TableCsvExportProps<T extends { id?: string | number }> = Omit<
  UseTableCsvExportOptions<T>,
  "definition" | "records"
> & {
  definition: CsvExportDefinition<T>;
  children: ReactElement<ExportFieldProps>;
  exportBarProps?: BoxProps;
};

export const TableCsvExport = <T extends { id?: string | number }>({
  definition,
  children,
  exportBarProps,
  ...options
}: TableCsvExportProps<T>) => {
  const { data = [] } = useListContext();
  const activeDefinition = useMemo(
    () => ({
      ...definition,
      columns: getActiveCsvColumns(definition.columns, children.props.children)
    }),
    [children.props.children, definition]
  );
  const exportState = useTableCsvExport({
    ...options,
    definition: activeDefinition,
    records: data as T[]
  });

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={1} {...exportBarProps}>
        <TableCsvDownloadMenu exportState={exportState} />
      </Box>
      {children}
    </>
  );
};
